<template>
  <sp-alert v-if="!model.isSelfPay" :variant="variant" :type="model.type" :append-align="appendAlign">
    <sp-icon slot="prepend" class="prepend-icon" :name="model.icon"></sp-icon>

    <div class="content">
      <sp-alert-content>
        <template v-if="model.isUndefined">
          <strong>{{ t("add_insurance_plan") }}</strong> {{ t("network_check_prompt") }}
        </template>

        <template v-else>
          <WideLayoutContent :model="model" />

          <InsurancePlanFilterInline
            :title="t('change_plan')"
            :insurance-plan="insurancePlan"
            :non-clearable="nonClearable"
            @insurance-changed="insuranceChanged"
          />

          <p v-if="model.isNotAccepted">
            {{ t("higher_copay_notice") }}
          </p>
        </template>
      </sp-alert-content>
      <InsurancePlanFilterOutlined
        v-if="model.isUndefined"
        :insurance-plan="insurancePlan"
        :non-clearable="nonClearable"
        @insurance-changed="insuranceChanged"
      />
    </div>
  </sp-alert>

  <div class="footer" :class="breakpointModifierClass">
    <sp-button v-if="showGoToProviderFinder" @click="gotToProviderFinder">{{
      t("find_in_network_providers")
    }}</sp-button>
    <sp-provider-insurance-plans-modal
      v-if="!model.isNoProviderData"
      :accepted-plans-count="model.provider.insurancePlanCount"
      class="sp-insurance-app__plans-modal"
      :url="`${physicianPath}/insurance_plans`"
      :trigger-label="acceptedPlansTriggerLabel"
      :modal-title="t('all_accepted_insurance_plans')"
    ></sp-provider-insurance-plans-modal>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import InsurancePlanFilterInline from "./InsurancePlanFilterInline.ce.vue";
import InsurancePlanFilterOutlined from "./InsurancePlanFilterOutlined.ce.vue";
import WideLayoutContent from "./WideLayoutContent.ce.vue";
import { booleanAttribute } from "#ce/utils/props";

const props = defineProps({
  model: {
    type: Object,
    required: true,
  },
  insurancePlan: {
    type: String,
    default: undefined,
  },
  physiciansUrl: {
    type: String,
    required: true,
  },
  physicianPath: {
    type: String,
    required: true,
  },
  acceptedLabel: {
    type: String,
    default: "Accepts Your Insurance",
  },
  nonClearable: booleanAttribute(),
});

const { t } = useI18n();

const acceptedPlansTriggerLabel = computed(() => {
  const count = props.model.provider.insurancePlanCount;

  return t("view_all_accepted_plans", { count }, count);
});

const appendAlign = computed(() => (props.model.isUndefined ? "center" : undefined));
const variant = computed(() => (props.model.isUndefined ? "elevated" : undefined));

const showGoToProviderFinder = computed(() => !props.model.isAccepted && !props.model.isUndefined);

function gotToProviderFinder() {
  const { searchParams } = new URL(document.location.href);
  const searchUrl = searchParams.get("search_url");

  window.location = searchUrl ?? props.physiciansUrl;
}
const { ltModifiers } = useBreakpoints();

const breakpointModifierClass = computed(() => [...ltModifiers.value]);

const emit = defineEmits(["insurance-changed"]);

function insuranceChanged(value) {
  emit("insurance-changed", value);
}
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.content {
  display: grid;
  grid-template-columns: 1fr max-content;
  column-gap: 0.5ch;
}

@media screen and (max-width: 1000px) {
  .content {
    grid-template-columns: 1fr;
    column-gap: 0;
    row-gap: 0.5rem;
  }
}

.footer {
  display: flex;
  align-items: center;
  gap: 1ch;
  margin-top: var(--sp-comp-insurance-app-footer-margin-top, var(--sp-ref-spacing-8));

  &.--lt-sm-screen {
    flex-direction: column;
    gap: 0.5rem;
    .sp-insurance-app__plans-modal,
    sp-button {
      --sp-comp-button-width: 100%;
    }
  }
}
</style>
